/**
 * useAuth — React hook for auth state.
 */

import { useSyncExternalStore, useCallback } from "react";
import {
  subscribe,
  getUser,
  isAuthenticated,
  startLogin,
  startOtp,
  verifyOtp,
  logout,
  switchWorkspace,
  createNewWorkspace,
  getCurrentWorkspaceId,
  type AuthUser,
} from "../auth";

interface UseAuthResult {
  isAuthenticated: boolean;
  user: AuthUser | null;
  currentWorkspaceId: string | null;
  login: (provider?: "google") => Promise<void>;
  requestOtp: (email: string) => Promise<{ ok: boolean; challengeId?: string; error?: string }>;
  verifyOtp: (challengeId: string, code: string) => Promise<{ ok: boolean; error?: string; attemptsRemaining?: number }>;
  logout: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<boolean>;
  createWorkspace: (name: string) => Promise<{ id: string; slug: string; name: string } | null>;
}

let cachedSnapshot = { user: getUser(), authenticated: isAuthenticated() };

function getSnapshot(): { user: AuthUser | null; authenticated: boolean } {
  const user = getUser();
  const authenticated = isAuthenticated();
  if (user !== cachedSnapshot.user || authenticated !== cachedSnapshot.authenticated) {
    cachedSnapshot = { user, authenticated };
  }
  return cachedSnapshot;
}

export function useAuth(): UseAuthResult {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const login = useCallback(async (provider?: "google") => {
    await startLogin(provider);
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    return startOtp(email);
  }, []);

  const doVerifyOtp = useCallback(async (challengeId: string, code: string) => {
    return verifyOtp(challengeId, code);
  }, []);

  const doLogout = useCallback(async () => {
    await logout();
  }, []);

  const doSwitchWorkspace = useCallback(async (workspaceId: string) => {
    const ok = await switchWorkspace(workspaceId);
    if (ok) window.location.reload();
    return ok;
  }, []);

  const doCreateWorkspace = useCallback(async (name: string) => {
    const ws = await createNewWorkspace(name);
    if (ws) window.location.reload();
    return ws;
  }, []);

  return {
    isAuthenticated: state.authenticated,
    user: state.user,
    currentWorkspaceId: getCurrentWorkspaceId(),
    login,
    requestOtp,
    verifyOtp: doVerifyOtp,
    logout: doLogout,
    switchWorkspace: doSwitchWorkspace,
    createWorkspace: doCreateWorkspace,
  };
}
